import { consultBookingUrl, starterKitBuyUrl, starterKitDemoUrl } from "@/lib/offers/external-url";
import type { PricePresentation } from "@/lib/offers/local-pricing";

export interface FixedOffer {
  key: string;
  name: string;
  summary: string;
  bullets: string[];
  ctaHref: string;
  ctaLabel: string;
  presentation: PricePresentation;
  period: "one-time" | "monthly";
  priceConfirmed: boolean;
  amount?: number | null;
  amountMax?: number | null;
  bookingUrl?: string | null;
  buyUrl?: string | null;
  /** Starter kit only: list price shown struck through once the price is confirmed. */
  compareAtAmount?: number | null;
  /** Starter kit only: launch price buyer limit for the badge. */
  launchLimit?: number | null;
  /** Starter kit only: live demo link. */
  demoUrl?: string | null;
}

export const hireFixedOfferFallbacks: FixedOffer[] = [
  {
    key: "audit",
    name: "DevOps and cloud cost audit",
    summary: "Fixed price. A written report within one week.",
    bullets: [
      "Functions, bandwidth, image optimization, caching, and database compute",
      "A prioritized list of what to change and what to leave alone",
      "Written report within one week of kickoff",
    ],
    ctaHref: "/contact?offer=audit",
    ctaLabel: "Ask about an audit",
    presentation: "from",
    period: "one-time",
    priceConfirmed: false,
  },
  {
    key: "rescue",
    name: "Rescue for an AI-built app",
    summary: "A flat fee to make a vibe-coded app safe to ship.",
    bullets: [
      "Keys moved out of the code and into env vars",
      "CI and deploys, including Vercel",
      "A Neon database, wired up properly",
      "A basic security pass",
    ],
    ctaHref: "/contact?offer=rescue",
    ctaLabel: "Ask about a rescue",
    presentation: "from",
    period: "one-time",
    priceConfirmed: false,
  },
  {
    key: "consult",
    name: "60-minute stack consult",
    summary: "A paid hour on the stack you are running.",
    bullets: [
      "React, Next.js, Vercel, Neon, Clerk, Stripe, or a close cousin",
      "Where the bill, deploys, or data model are getting fragile",
      "You leave with a short list of what to do next",
    ],
    ctaHref: "/contact?offer=consult",
    ctaLabel: "Ask for a consult",
    presentation: "on-request",
    period: "one-time",
    priceConfirmed: true,
    amount: 250,
  },
];

/** Page price for the paid hour. Leave this out of JSON-LD and llms.txt. */
export function consultListedAmount(): number | null {
  const amount = hireFixedOfferFallbacks.find((offer) => offer.key === "consult")?.amount;
  return typeof amount === "number" ? amount : null;
}

export const starterKitFallback: FixedOffer = {
  key: "starter-kit",
  name: "Next.js starter kit",
  summary: "A paid starter project: Next.js, Clerk, Stripe, and Neon, ready to clone.",
  bullets: [
    "Next.js app structure you can deploy",
    "Clerk for sign-in",
    "Stripe for payments",
    "Neon for Postgres",
  ],
  ctaHref: "/contact?offer=other",
  ctaLabel: "Ask about the starter kit",
  presentation: "on-request",
  period: "one-time",
  priceConfirmed: true,
  amount: 79,
  compareAtAmount: 129,
};

export interface SanityFixedOffer {
  key?: string | null;
  offer?: string | null;
  name?: string | null;
  audience?: string | null;
  bullets?: string[] | null;
  amount?: number | null;
  amountMax?: number | null;
  period?: string | null;
  priceConfirmed?: boolean | null;
  priceStyle?: string | null;
  bookingUrl?: string | null;
  buyUrl?: string | null;
  compareAtAmount?: number | null;
  launchLimit?: number | null;
  demoUrl?: string | null;
}

function asOffer(tier: SanityFixedOffer, fallback?: FixedOffer): FixedOffer | null {
  const key = tier.key || tier.offer || fallback?.key;
  const name = tier.name || fallback?.name;
  const bullets = (tier.bullets || fallback?.bullets || []).filter(
    (bullet): bullet is string => Boolean(bullet),
  );
  if (!key || !name || bullets.length === 0) return null;

  return {
    key,
    name,
    summary: tier.audience || fallback?.summary || "",
    bullets,
    ctaHref: fallback?.ctaHref || "/contact",
    ctaLabel: fallback?.ctaLabel || "Contact me",
    presentation: tier.priceStyle === "on-request" ? "on-request" : "from",
    period: tier.period === "monthly" ? "monthly" : "one-time",
    priceConfirmed: tier.priceConfirmed === true,
    amount: typeof tier.amount === "number" ? tier.amount : null,
    amountMax: typeof tier.amountMax === "number" ? tier.amountMax : null,
    bookingUrl: key === "consult" ? consultBookingUrl(tier.bookingUrl) : null,
    buyUrl: key === "starter-kit" ? starterKitBuyUrl(tier.buyUrl) : null,
    compareAtAmount: typeof tier.compareAtAmount === "number" ? tier.compareAtAmount : null,
    launchLimit: typeof tier.launchLimit === "number" ? tier.launchLimit : null,
    demoUrl: key === "starter-kit" ? starterKitDemoUrl(tier.demoUrl) : null,
  };
}

export function mergeFixedOffers(
  fallbacks: FixedOffer[],
  tiers: SanityFixedOffer[] | null | undefined,
): FixedOffer[] {
  const incoming = new Map<string, FixedOffer>();
  for (const tier of tiers || []) {
    const key = tier.key || tier.offer;
    const fallback = fallbacks.find((item) => item.key === key);
    const offer = asOffer(tier, fallback);
    if (offer) incoming.set(offer.key, offer);
  }

  const merged = fallbacks.map((fallback) => {
    const override = incoming.get(fallback.key);
    if (!override) {
      return {
        ...fallback,
        bookingUrl: fallback.key === "consult" ? consultBookingUrl(null) : null,
        buyUrl: fallback.key === "starter-kit" ? starterKitBuyUrl(null) : null,
        demoUrl: fallback.key === "starter-kit" ? starterKitDemoUrl(null) : null,
      };
    }
    incoming.delete(fallback.key);
    const sanityConfirmed = override.priceConfirmed && typeof override.amount === "number";
    if (!sanityConfirmed && fallback.priceConfirmed) {
      // An unconfirmed Sanity tier keeps the agreed redesign price.
      return {
        ...override,
        presentation: fallback.presentation,
        priceConfirmed: true,
        amount: fallback.amount ?? null,
        amountMax: fallback.amountMax ?? null,
        compareAtAmount: override.compareAtAmount ?? fallback.compareAtAmount ?? null,
      };
    }
    return override;
  });

  return [...merged, ...incoming.values()];
}

export const hireOffersQuery = `*[_type == "pricingTier" && offer in ["audit", "rescue", "consult"] && !(_id in path("drafts.**"))] | order(order asc) {
  "key": offer,
  offer,
  name,
  "audience": audience,
  bullets,
  amount,
  amountMax,
  period,
  priceConfirmed,
  priceStyle,
  bookingUrl
}`;

export const starterKitQuery = `*[_type == "pricingTier" && offer == "starter-kit" && !(_id in path("drafts.**"))] | order(order asc) {
  "key": offer,
  offer,
  name,
  "audience": audience,
  bullets,
  amount,
  amountMax,
  period,
  priceConfirmed,
  priceStyle,
  buyUrl,
  compareAtAmount,
  launchLimit,
  demoUrl
}`;
